import { useState } from "react";
import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import { reprocessarCobrancas } from "@/services/consignado/proposta-cobranca.service";
import { hojeYYYYMMDD, inicioDoMesYYYYMMDD } from "../VinculosAtivosRedesign/dateYYYYMMDD";
import { formLabelProps } from "../VinculosAtivosRedesign/modalStyles";
import { useApiErrorToast } from "@/hooks/useApiErrorToast";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

// Reprocessamento é global (todas as CCBs), não por CCB: o backend ingere as
// 4 fontes da BMP no período e roda as etapas por flag em background. A
// resposta chega na hora; o resultado aparece nas telas conforme o job avança.
export default function ReprocessarCobrancasModal({ isOpen, onClose, onSuccess }: Props) {
  const toast = useToast();
  const toastErro = useApiErrorToast();
  const [dataInicio, setDataInicio] = useState<string>(inicioDoMesYYYYMMDD);
  const [dataFim, setDataFim] = useState<string>(hojeYYYYMMDD);

  const periodoInvalido = !dataInicio || !dataFim || dataInicio > dataFim;

  const handleClose = () => {
    setDataInicio(inicioDoMesYYYYMMDD());
    setDataFim(hojeYYYYMMDD());
    onClose();
  };

  const reprocessar = useMutation({
    mutationFn: async () => await reprocessarCobrancas({ data_inicio: dataInicio, data_fim: dataFim }),
    onSuccess(retorno) {
      if (retorno.status === "EM_ANDAMENTO") {
        toast({
          title: "Reprocessamento já em andamento",
          description: "Aguarde a execução atual terminar antes de disparar outra.",
          status: "info",
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      handleClose();
      toast({
        title: "Reprocessamento solicitado",
        description:
          "Ingestão e conciliação disparadas em segundo plano. Atualize em instantes para ver os novos status.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      onSuccess?.();
    },
    onError: toastErro("Erro ao reprocessar"),
  });

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Reprocessar cobranças</ModalHeader>
        <ModalBody>
          <Text fontSize="sm" mb={4}>
            Busca na bancarizadora as escriturações, repasses, términos e novos vínculos do
            período e reconcilia as competências de <Text as="span" fontWeight="bold">todas as CCBs</Text>.
            Roda em segundo plano.
          </Text>
          <Flex gap={3}>
            <FormControl isRequired>
              <FormLabel {...formLabelProps}>Data início</FormLabel>
              <Input
                type="date"
                size="sm"
                value={dataInicio}
                max={dataFim}
                onChange={(e) => setDataInicio(e.target.value)}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel {...formLabelProps}>Data fim</FormLabel>
              <Input
                type="date"
                size="sm"
                value={dataFim}
                min={dataInicio}
                max={hojeYYYYMMDD()}
                onChange={(e) => setDataFim(e.target.value)}
              />
            </FormControl>
          </Flex>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button variant="ghost" size="sm" onClick={handleClose} isDisabled={reprocessar.isPending}>
            Cancelar
          </Button>
          <Button
            colorScheme="primary"
            size="sm"
            onClick={() => reprocessar.mutate()}
            isLoading={reprocessar.isPending}
            isDisabled={periodoInvalido}
          >
            Reprocessar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
